import React, { useEffect, useState } from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Home from './components/Pages/Home';
import Blog from './components/Pages/Blog';
import BlogDetails from './components/Pages/BlogDetails';
import Services from './components/Pages/Services';
import ErrorPage from './components/Pages/ErrorPage';
import { useTranslation } from 'react-i18next';
import languages from './config/languages';
import { BlogArticleProvider } from './contexts/BlogArticleContext';
import LegalText from './components/Pages/LegalText';
import UsSpainComp from './components/Pages/UsSpain';
import GestlifeGuaranteeLayout from './components/Pages/GuaranteeSpain';
import FivSpain from './components/Pages/FivSpain';
import SurrogacySpain from './components/Pages/SurrogacySpain';
import TestimonialSpainText from './components/TestiominalSpaintext';
import TestimonialSpain from './components/Pages/TestimonialSpain';

// Función para cargar scripts de manera dinámica
const loadScript = (src, id, callback) => {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('El objeto document no está disponible (SSR/prerender).'));
      return;
    }
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.id = id;
    script.async = true;
    script.onload = () => {
      if (callback) callback();
      resolve();
    };
    script.onerror = () => reject(new Error(`Error al cargar el script: ${src}`));
    const appendScript = () => {
      document.body.appendChild(script);
    };
    if (document.readyState === 'complete' || document.body) {
      appendScript();
    } else {
      document.addEventListener('DOMContentLoaded', appendScript, { once: true });
    }
  });
};

function App() {
  const { pathname } = useLocation();
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const { t, i18n, ready } = useTranslation();

  // Idioma principal (predeterminado)
  const defaultLanguage = 'es';
  const supportedLanguages = Object.keys(languages);

  // Scroll al inicio al cambiar de ruta
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  // Verificar idioma en la URL y establecer el idioma predeterminado
  useEffect(() => {
    const pathSegments = pathname.split('/');
    const pathLang = pathSegments[1];
    if (supportedLanguages.includes(pathLang)) {
      if (i18n.language !== pathLang) {
        i18n.changeLanguage(pathLang);
      }
    } else {
      if (i18n.language !== defaultLanguage) {
        i18n.changeLanguage(defaultLanguage);
      }
    }
  }, [pathname, i18n, supportedLanguages, defaultLanguage]);

  // Cargar scripts tras interacción del usuario
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.snapSaveState) return; // Evita cargar en prerender
    const handleUserInteraction = () => {
      if (!scriptsLoaded) {
        window.axeptioSettings = {
          clientId: '66865e9cc205eb89f700fcfe',
          cookiesVersion: t('cookies_language'),
          googleConsentMode: {
            default: {
              analytics_storage: 'denied',
              ad_storage: 'denied',
              ad_user_data: 'denied',
              ad_personalization: 'denied',
              wait_for_update: 500,
            },
          },
        };
        loadScript('//static.axept.io/sdk.js', 'axeptio-sdk')
          .then(() => {})
          .catch((error) => { console.error(error); });
        loadScript(t('tawkto_language'), 'tawkto-script')
          .then(() => {})
          .catch((error) => { console.error(error); });
        loadScript('https://assets.calendly.com/assets/external/widget.js', 'calendly-widget')
          .then(() => {})
          .catch((error) => { console.error(error); });
        loadScript('//embed.typeform.com/next/embed.js', 'typeform-embed')
          .then(() => {})
          .catch((error) => { console.error(error); });
        setScriptsLoaded(true);
      }
    };
    const events = ['click', 'scroll', 'keydown', 'touchstart'];
    events.forEach((event) => {
      window.addEventListener(event, handleUserInteraction, { once: true, passive: true });
    });
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [scriptsLoaded, t]);

  if (!ready) {
    return null;
  }

  return (
    <BlogArticleProvider>
      <Routes>
        {/* BLOQUE SIN PREFIJO: Ejemplo -> /blog/ */}
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          {/* Ruta explícita para 404 */}
          <Route path="404" element={<ErrorPage />} />
          {/* Blog: /blog/ */}
          <Route path="blog/" element={<Blog />} />
          {/* Redirección para /blog/page/ sin número */}
          <Route path="blog/page" element={<Navigate to="/blog/" replace />} />
          {/* Paginación: /blog/page/:page/ */}
          <Route path="blog/page/:page/" element={<Blog />} />
          {/* Detalle: /blog/mi-articulo/ */}
          <Route path="blog/:slug/" element={<BlogDetails />} />
          {/* Servicios sin prefijo => /servicioX/ */}
          <Route path=":slug/" element={<Services />} />
          {/* Página legal: /legalt/ */}
          <Route path="legalt" element={<LegalText />} />
          {/* Ruta comodín */}
          <Route path="*" element={<ErrorPage />} />
        </Route>

        {/* BLOQUE CON PREFIJO: Ejemplo -> /en/blog/ */}
        {supportedLanguages.map((lang) => {
          if (lang === 'es-es') {
            return (
              <Route key={lang} path={`/${lang}/*`} element={<Layout />}>
                <Route index element={<Navigate to="legalt" replace />} />
                <Route path="legalt" element={<LegalText />} />
                 <Route path="nosotros" element={<UsSpainComp />} />
                 <Route path="garantias" element={<GestlifeGuaranteeLayout />} />
                 <Route path="programasfiv" element={<FivSpain />} />
                 <Route path="gestacion-subrogada-espana" element={<SurrogacySpain />} />
                 <Route path="testimonios" element={<TestimonialSpain />} />
                <Route path="*" element={<Navigate to="legalt" replace />} />
              </Route>
            );
          }
          return (
            <Route key={lang} path={`/${lang}/`} element={<Layout />}>
              <Route index element={<Home />} />
              {/* Blog: /en/blog/ */}
              <Route path="blog/" element={<Blog />} />
              {/* Redirección para /en/blog/page/ */}
              <Route path="blog/page" element={<Navigate to={`/${lang}/blog/`} replace />} />
              {/* Paginación: /en/blog/page/:page/ */}
              <Route path="blog/page/:page/" element={<Blog />} />
              <Route path="blog/:slug/" element={<BlogDetails />} />
              <Route path=":slug/" element={<Services />} />
              <Route path="*" element={<ErrorPage />} />
            </Route>
          );
        })}

        {/* Fallback global */}
        <Route path="*" element={<ErrorPage />} />
      </Routes>
    </BlogArticleProvider>
  );
}

export default App;